import { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { UIText } from '../ui-text';
import styles from './ui-segmented-control2.module.scss';
import { blue, concrete } from '../../styles';

type UISegmentedControl2Props = {
    items: UISegmentedControlItemProps[];
    selectedIndex: number;
    selectedIndexChanged: (index: number) => void;
};

export function UISegmentedControl2(
    props: UISegmentedControl2Props
): ReactElement {
    const { items, selectedIndex, selectedIndexChanged } = props;

    const [widths, setWidths] = useState<number[]>(
        Array.from({ length: items.length }, () => 0)
    );

    const onWidthChange = useCallback((index: number, width: number) => {
        setWidths((prev) => {
            const newWidths = [...prev];
            newWidths[index] = width;
            return newWidths;
        });
    }, []);

    const getSelectedWidth = () => {
        if (selectedIndex < 0 || selectedIndex >= widths.length) return 0;
        return widths[selectedIndex];
    };

    const getSelectedOffset = () => {
        let offset = 0;
        for (let i = 0; i < selectedIndex; i++) {
            offset += widths[i];
        }
        return offset;
    };

    return (
        <div style={{ display: 'flex' }}>
            <div
                style={{
                    display: 'flex',
                    border: `1px solid ${concrete}`,
                    borderRadius: 8,
                    position: 'relative',
                    overflow: 'hidden'
                }}
            >
                <div
                    style={{
                        position: 'absolute',
                        transform: `translateX(${getSelectedOffset()}px)`,
                        width: getSelectedWidth(),
                        height: '100%',
                        backgroundColor: blue,
                        borderRadius: 7,
                        transition: 'transform 0.2s ease-in-out'
                    }}
                />
                <div style={{ zIndex: 1 }}>
                    {items.map((itemProps, index) => (
                        <UISegmentedControlItem
                            index={index}
                            key={index}
                            {...itemProps}
                            isSelected={selectedIndex == index}
                            onClick={() => selectedIndexChanged(index)}
                            onWidthChange={onWidthChange}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

type UISegmentedControlItemProps = {
    text: string;
    width?: number;
};

function UISegmentedControlItem(
    props: UISegmentedControlItemProps & {
        index: number;
        isSelected: boolean;
        onClick: () => void;
        onWidthChange: (index: number, width: number) => void;
    }
): ReactElement {
    const { text, width, index, isSelected, onClick, onWidthChange } = props;

    const buttonRef = useRef<HTMLButtonElement>(null);
    const [buttonWidth, setButtonWidth] = useState(0);

    useEffect(() => {
        if (!buttonRef.current) return;

        // Function to update the width state
        const updateWidth = () => {
            if (!buttonRef.current) return;
            setButtonWidth(buttonRef.current.offsetWidth);
        };

        // Create a ResizeObserver to monitor width changes
        const resizeObserver = new ResizeObserver(() => {
            updateWidth();
        });

        // Start observing the button element
        resizeObserver.observe(buttonRef.current);

        // Set initial width
        updateWidth();

        // Cleanup function to disconnect the observer when the component unmounts
        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        onWidthChange(index, buttonWidth);
    }, [onWidthChange, index, buttonWidth]);

    return (
        <button
            ref={buttonRef}
            className={styles.item}
            style={{ width, zIndex: 1 }}
            onClick={onClick}
        >
            <UIText textAlign="center" type={isSelected ? 'white' : 'default'}>
                {text}
            </UIText>
        </button>
    );
}
